import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';
import qs from 'query-string';

import useOpenAlert from '../../../hooks/useOpenAlert';
import { loginSuccess } from '../../../actions/auth-actions';
import {
  accountCreateByEmail as accountCreateByEmailMutation,
  accountUpdate as accountUpdateMutation,
} from '../../../graphql/mutations/account';
import { ACCOUNT_PRO_ROLES } from '../../../constants/account-roles';
import { getErrorString } from '../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../util/sentry';
import { gtm } from '../../../util/analytics';

/**
 * Registers an account - AppSumo version.
 *
 * @returns {{onRegister: (function(*): Promise<undefined>), loading: boolean}}
 */
const useAppSumoRegister = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const openErrorModal = useOpenAlert();
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const [accountCreateByEmail] = useMutation(accountCreateByEmailMutation);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const source = qs.parse(location.search)?.source ?? '';

  const onRegister = async values => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const response = await accountCreateByEmail({
        variables: {
          input: {
            email: values.email_address,
            name: `${values.first_name} ${values.last_name}`.trim(),
            password: values.password,
          },
        },
      });
      const accountId = response.data.accountCreateByEmail.account_id;

      await accountUpdate({
        variables: {
          input: {
            id: accountId,
            pro_roles: [ACCOUNT_PRO_ROLES.PHOTOGRAPHER.toUpperCase()],
            organization_name: values.organization_name,
            client_store_items: [
              {
                key: 'pro_registration_persona',
                value: 'appsumo',
              },
              {
                key: 'app_sumo_code',
                value: values.app_sumo_code || '',
              },
            ],
          },
        },
      });

      gtm.trackEvent({
        type: 'Register as PRO',
        action: 'APP_SUMO',
        label: ACCOUNT_PRO_ROLES.PHOTOGRAPHER,
        source,
      });

      // Forces log in and redirect to /app
      onLogin();
    } catch (responseError) {
      openErrorModal({
        message: 'There was an error with your registration.',
        info: responseError?.request_id ?? getErrorString(responseError),
      });

      sentryCaptureMessage('AppSumo Registration Error', {
        level: 'error',
        extra: {
          error: responseError,
          email: values.email_address,
        },
      });
      setLoading(false);
    }
  };

  return {
    onRegister,
    loading,
  };
};

export default useAppSumoRegister;
